import type { LoggerEvent, StartRumEvent } from '@/types/logger.types'
import { isIframe, random } from '@/utils/helpers'
import { loadScript } from '@/utils/loadScript'

import type { ActiveCmps } from './header-bidding/types'
import { AdConsoleLogger } from './utils/ad-console-logger'
import { createRumTracker } from './utils/create-rum-tracker'

type ConsentSource =
    | 'funding_choices'
    | 'existing_tcf'
    | 'existing_gpp'
    | 'existing_tcf_gpp'
    | 'mock'
    | 'no_cmp'
    | 'error'

type Options = {
    consentAccount: string
    handleConsentlessEnv: () => Promise<boolean>
    logEvent: (event: LoggerEvent) => void
    startRumEvent?: StartRumEvent
}

export enum GoogleFcAdBlockerStatus {
    UNKNOWN = 0,
    EXTENSION_LEVEL_AD_BLOCKER = 1,
    NETWORK_LEVEL_AD_BLOCKER = 2,
    NO_AD_BLOCKER = 3,
}

export interface GoogleFC {
    controlledLoading?: boolean
    callbackQueue?: { [event: string]: () => void }[]
    showRevocationMessage?: () => void
    getAdBlockerStatus?: () => number
    AdBlockerStatusEnum?: Record<string, GoogleFcAdBlockerStatus>
}

type GppState = {
    gppVersion: string
    cmpStatus: string
    cmpDisplayStatus: string
    signalStatus: string
    cmpId: number
    supportedAPIs: string[]
    sectionList: number[]
    applicableSections: number[]
    gppString: string
    parsedSections: Record<string, Record<string, unknown>[]>
}

type GppCallback = (result: unknown, success: boolean) => void

declare global {
    interface Window {
        __gpp?: (command: string, callback: GppCallback, parameter?: unknown, version?: unknown) => void
        __uspapi?: (command: string, version: unknown, callback: GppCallback) => void
        googlefc?: GoogleFC
    }
}

export class ConsentManager {
    private readonly PROBE_TIMEOUT = 1000
    private readonly PROBE_CALL_ID = 'pg-probe'

    private resolveReady = () => {}
    ready: Promise<void>

    consentSource: ConsentSource | null = null

    adConsoleLogger = new AdConsoleLogger('consent')

    private uspString = '1YNN'

    private usnatState: Record<string, unknown> = {
        Version: 1,
        SharingNotice: 1,
        SaleOptOutNotice: 1,
        SharingOptOutNotice: 1,
        TargetedAdvertisingOptOutNotice: 1,
        SensitiveDataProcessingOptOutNotice: 0,
        SensitiveDataLimitUseNotice: 0,
        SaleOptOut: 2,
        SharingOptOut: 2,
        TargetedAdvertisingOptOut: 2,
        SensitiveDataProcessing: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        KnownChildSensitiveDataConsents: [0, 0],
        PersonalDataConsents: 0,
        MspaCoveredTransaction: 2,
        MspaOptOutOptionMode: 0,
        MspaServiceProviderMode: 0,
    }

    private gppState: GppState = {
        gppVersion: '1.1',
        cmpStatus: 'loaded',
        cmpDisplayStatus: 'hidden',
        signalStatus: 'ready',
        supportedAPIs: ['7:usnat'],
        cmpId: 300,
        sectionList: [7],
        applicableSections: [7],
        gppString: 'DBABL~BVQqAAAAAg',
        parsedSections: {
            usnat: [this.usnatState],
        },
    }

    private options: Options

    constructor(options: Options) {
        this.options = options
        ;({ promise: this.ready, resolve: this.resolveReady } = Promise.withResolvers())
    }

    public async install(): Promise<ActiveCmps> {
        try {
            this.adConsoleLogger.debug('install')
            const isMainFrame = !isIframe()

            if (isMainFrame) {
                const { startRumEvent } = this.options
                const tracker = startRumEvent ? createRumTracker('consent-funding-choices-load', startRumEvent) : null
                await loadScript({
                    src: `https://fundingchoicesmessages.google.com/i/${this.options.consentAccount}?ers=3`,
                    useExisting: true,
                })
                tracker?.send({ status: 'success' })
                this.consentSource = 'funding_choices'
                this.adConsoleLogger.debug('install: success')
                return { gpp: true, tcf: true }
            }

            const [isConsentless, hasGppLocator, hasTcfLocator] = await Promise.all([
                this.checkConsentlessEnv(),
                this.probeGppLocator(),
                this.probeTcfLocator(),
            ])

            if (hasTcfLocator && hasGppLocator) {
                this.consentSource = 'existing_tcf_gpp'
                this.adConsoleLogger.debug('existing CMP detected', this.consentSource)
            } else if (hasTcfLocator || hasGppLocator) {
                this.consentSource = hasTcfLocator ? 'existing_tcf' : 'existing_gpp'
                this.adConsoleLogger.debug('existing CMP detected', this.consentSource)
            } else if (isConsentless) {
                this.adConsoleLogger.debug('unable to install: consentless env detected but no CMP found')
                this.consentSource = 'mock'
                this.installMock()
                this.adConsoleLogger.debug('mock installed')
                return { gpp: true, tcf: true }
            } else {
                this.consentSource = 'no_cmp'
            }

            return { gpp: hasGppLocator, tcf: hasTcfLocator }
        } catch (e) {
            this.adConsoleLogger.error('install: error', e instanceof Error ? (e as Error).message : String(e))
            this.consentSource = 'error'
            return { gpp: false, tcf: false }
        } finally {
            this.logInitEvent()
            this.resolveReady()
            this.subscribeConsentChanges()
        }
    }

    /**
     * Consentless-env check — its long pole is the (untimed) getCountryCode() call behind
     * handleConsentlessEnv. Tracked as its own RUM op so a slow geo lookup is visible instead
     * of hiding inside the parallel consent install.
     */
    private async checkConsentlessEnv(): Promise<boolean> {
        const { startRumEvent } = this.options
        const tracker = startRumEvent ? createRumTracker('consent-consentless-check', startRumEvent) : null
        const isConsentless = await this.options.handleConsentlessEnv()
        tracker?.send({ status: 'success' })
        return isConsentless
    }

    private logInitEvent(): void {
        const detectedApis = [
            typeof window.__tcfapi === 'function' && 'tcf',
            typeof window.__gpp === 'function' && 'gpp',
            typeof window.__uspapi === 'function' && 'uspapi',
        ].filter(Boolean)

        const detectedApisStr = detectedApis.join(',') || 'none'
        const consentSource = this.consentSource || 'unknown'

        this.adConsoleLogger.debug(`init | source: ${consentSource} | detected APIs: ${detectedApisStr}`)
        this.options.logEvent({
            event: 'custom_event',
            eventName: 'consent_init',
            dimension1: consentSource,
            dimension2: detectedApisStr,
        })
    }

    private subscribeConsentChanges(): void {
        if (this.consentSource === 'funding_choices') {
            this.subscribeFundingChoices()
            this.subscribeTcf()
        }

        if (this.consentSource === 'existing_tcf' || this.consentSource === 'existing_tcf_gpp') {
            this.subscribeTcf()
        }

        if (this.consentSource === 'existing_gpp' || this.consentSource === 'existing_tcf_gpp') {
            this.subscribeGpp()
        }
    }

    private subscribeFundingChoices(): void {
        window.googlefc = window.googlefc ?? {}
        window.googlefc.callbackQueue = window.googlefc.callbackQueue ?? []
        window.googlefc.callbackQueue.push({
            CONSENT_API_READY: () => this.logConsentChange('loaded', 'CONSENT_API_READY'),
            // getTCData is deprecated/unreliable here - accept/decline comes from subscribeTcf's useractioncomplete.
            CONSENT_DATA_READY: () => this.logConsentChange('loaded', 'CONSENT_DATA_READY'),
        })
    }

    private subscribeTcf(): void {
        window.__tcfapi?.('addEventListener', 2, (tcData, success) => {
            if (!success || !tcData) {
                return
            }

            switch (tcData.eventStatus) {
                case 'tcloaded': {
                    this.logConsentChange('loaded', 'tcloaded')
                    break
                }
                case 'cmpuishown': {
                    this.logConsentChange('shown', 'cmpuishown')
                    break
                }
                case 'useractioncomplete': {
                    this.logConsentChange(tcData.purpose?.consents?.[1] ? 'accepted' : 'declined', 'useractioncomplete')
                    break
                }
            }
        })
    }

    private subscribeGpp(): void {
        window.__gpp?.('addEventListener', (result, success) => {
            const data = result as { eventName?: string; data?: string } | null
            if (success && data?.eventName === 'signalStatus' && data?.data === 'ready') {
                window.__gpp?.(
                    'getSection',
                    (section, sectionSuccess) => {
                        if (sectionSuccess) {
                            const usnat = (section as Record<string, unknown>[] | null)?.[0]
                            const hasConsent = usnat?.TargetedAdvertisingOptOut !== 1
                            this.logConsentChange(hasConsent ? 'accepted' : 'declined', 'signalStatus')
                        }
                    },
                    'usnat',
                )
            }
        })
    }

    private logConsentChange(label: string, rawEvent: string): void {
        this.adConsoleLogger.debug(`consent changed | event: ${rawEvent} | status: ${label}`)
        this.options.logEvent({
            event: 'custom_event',
            eventName: 'consent_change',
            dimension1: label,
            dimension2: rawEvent,
        })
    }

    private probeLocator(callKey: string, returnKey: string, version: string | number): Promise<boolean> {
        return new Promise((resolve) => {
            let settled = false

            const handler = (event: MessageEvent): void => {
                const msg = event.data
                if (msg && msg[returnKey] && msg[returnKey].callId === this.PROBE_CALL_ID) {
                    settled = true
                    window.removeEventListener('message', handler)
                    resolve(true)
                }
            }

            window.addEventListener('message', handler)

            try {
                window.parent.postMessage(
                    {
                        [callKey]: {
                            command: 'ping',
                            version,
                            callId: this.PROBE_CALL_ID,
                        },
                    },
                    '*',
                )
            } catch {
                // Silently handle postMessage errors
            }

            setTimeout(() => {
                if (!settled) {
                    window.removeEventListener('message', handler)
                    resolve(false)
                }
            }, this.PROBE_TIMEOUT)
        })
    }

    private probeGppLocator(): Promise<boolean> {
        return this.probeLocator('__gppCall', '__gppReturn', '1.1')
    }

    private probeTcfLocator(): Promise<boolean> {
        return this.probeLocator('__tcfapiCall', '__tcfapiReturn', 2)
    }

    private installMock(): void {
        this.adConsoleLogger.debug('No CMP detected, installing PG mock')
        this.installLocator('__gppLocator')
        this.installLocator('__uspapiLocator')
        // TODO implement for EU?
        this.installLocator('__tcfapiLocator')
        this.installGppApi()

        window.addEventListener('message', this.messageHandler, false)

        if (typeof window.dispatchEvent === 'function') {
            window.dispatchEvent(new CustomEvent('gpploaded'))
            window.dispatchEvent(new CustomEvent('uspapiloaded'))
        }
    }

    private installLocator(name: string) {
        // @ts-ignore wrong types
        if (!window.frames[name]) {
            const iframe = document.createElement('iframe')
            iframe.name = name
            iframe.style.display = 'none'
            document.documentElement.appendChild(iframe)
        }
    }

    private installGppApi(): void {
        window.__gpp = (command, callback, parameter) => {
            this.adConsoleLogger.debug('__gpp', command)
            let result = null
            let success = true

            switch (command) {
                case 'ping':
                    result = {
                        gppVersion: this.gppState.gppVersion,
                        cmpStatus: this.gppState.cmpStatus,
                        cmpDisplayStatus: this.gppState.cmpDisplayStatus,
                        signalStatus: this.gppState.signalStatus,
                        supportedAPIs: this.gppState.supportedAPIs,
                        cmpId: this.gppState.cmpId,
                        gppString: this.gppState.gppString,
                        sectionList: this.gppState.sectionList,
                        applicableSections: this.gppState.applicableSections,
                    }
                    break
                case 'getGPPData':
                    result = this.gppState
                    break
                case 'hasSection':
                    result = parameter === 'usnat' || parameter === 7
                    break
                case 'getSection':
                    if (parameter === 'usnat' || parameter === 7) {
                        result = this.gppState.parsedSections.usnat
                    } else {
                        result = null
                    }
                    break
                case 'getField':
                    if (typeof parameter === 'string' && parameter.startsWith('usnat.')) {
                        const field = parameter.split('.')[1]!
                        result = this.usnatState[field] !== undefined ? this.usnatState[field] : null
                    } else {
                        result = null
                    }
                    break
                case 'addEventListener':
                    result = {
                        eventName: 'signalStatus',
                        listenerId: Math.floor(random() * 10000),
                        data: this.gppState.signalStatus,
                        pingData: {
                            gppVersion: this.gppState.gppVersion,
                            cmpStatus: this.gppState.cmpStatus,
                            cmpDisplayStatus: this.gppState.cmpDisplayStatus,
                            signalStatus: this.gppState.signalStatus,
                            supportedAPIs: this.gppState.supportedAPIs,
                            cmpId: this.gppState.cmpId,
                            gppString: this.gppState.gppString,
                            sectionList: this.gppState.sectionList,
                            applicableSections: this.gppState.applicableSections,
                        },
                    }
                    break
                case 'removeEventListener':
                    result = true
                    break
                default:
                    success = false
                    break
            }

            this.adConsoleLogger.debug(`__gpp response ("${command}")`, { result, success })

            if (typeof callback === 'function') {
                callback(result, success)
            }
        }

        window.__uspapi = (command, version, callback) => {
            this.adConsoleLogger.debug('__uspapi', { command, version })
            let result = null
            let success = false

            if (command === 'getUSPData') {
                result = { version: 1, uspString: this.uspString }
                success = true
            }

            this.adConsoleLogger.debug(`__uspapi response ("${command}")`, { result, success })
            if (typeof callback === 'function') {
                callback(result, success)
            }
        }
    }

    private messageHandler(event: MessageEvent) {
        let data
        try {
            data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data
        } catch (_e) {
            return
        }

        if (data && data.__gppCall) {
            const call = data.__gppCall
            window.__gpp?.(
                call.command,
                (result, success) => {
                    const returnMsg = {
                        __gppReturn: { returnValue: result, success, callId: call.callId },
                    }

                    if (event.source && typeof event.source.postMessage === 'function') {
                        event.source.postMessage(returnMsg, { targetOrigin: '*' })
                    }
                },
                call.parameter,
                call.version,
            )
        }

        if (data && data.__uspapiCall) {
            const call = data.__uspapiCall
            window.__uspapi?.(call.command, call.version, (result, success) => {
                const returnMsg = {
                    __uspapiReturn: { returnValue: result, success, callId: call.callId },
                }

                if (event.source && typeof event.source.postMessage === 'function') {
                    event.source.postMessage(returnMsg, { targetOrigin: '*' })
                }
            })
        }
    }
}
